import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PRODUCTS, accentBg } from '../data/products';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Minus, Plus, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function ProductDetailPage() {
  const { slug } = useParams();
  const product = PRODUCTS.find((p) => p.slug === slug);
  const { addItem } = useCart();
  const { format } = useCurrency();
  const [packSize, setPackSize] = useState('pack');
  const [qty, setQty] = useState(1);
  const [activeImage, setActiveImage] = useState(0);

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif-display text-4xl mb-4">Mask not found</h1>
        <Link to="/shop" className="btn-secondary">Back to shop</Link>
      </div>
    );
  }

  const gallery = product.gallery && product.gallery.length > 0 ? product.gallery : [product.image];
  const mainSrc = gallery[Math.min(activeImage, gallery.length - 1)];

  const unitPrice = packSize === 'pack' ? product.pack_price : product.single_price;
  const savings = product.single_price * product.pack_count - product.pack_price;

  const handleAdd = () => {
    addItem({ product, packSize, quantity: qty });
    toast.success(`Added ${product.name} to your bag.`);
  };

  return (
    <div data-testid={`product-page-${product.id}`} className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-20">
      <div className="md:col-span-7">
        <div className={`aspect-[4/5] ${accentBg(product.accent, true)} overflow-hidden`}>
          <img
            data-testid="pdp-main-image"
            src={mainSrc}
            alt={product.name}
            className="w-full h-full object-contain p-8 transition-opacity duration-300"
            key={mainSrc}
          />
        </div>
        {gallery.length > 1 && (
          <div className="grid grid-cols-5 gap-3 mt-4">
            {gallery.map((src, i) => (
              <button
                key={src}
                data-testid={`pdp-thumb-${i}`}
                onClick={() => setActiveImage(i)}
                className={`aspect-square ${accentBg(product.accent, true)} flex items-center justify-center overflow-hidden border-2 transition-all ${i === activeImage ? 'border-lum-text' : 'border-transparent opacity-70 hover:opacity-100'}`}
                aria-label={`View image ${i + 1}`}
              >
                <img src={src} alt={`${product.name} ${i + 1}`} className="w-full h-full object-contain p-2" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="md:col-span-5 md:sticky md:top-32 md:self-start">
        <p className="overline mb-3">{product.concern}</p>
        <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">{product.name}</h1>
        <p className="mt-2 text-lum-text2 italic font-serif-display text-xl">{product.tagline}</p>

        <div className="mt-6 flex items-baseline gap-3 flex-wrap">
          <span className="text-2xl">{format(unitPrice)}</span>
          {packSize === 'pack' && (
            <>
              <span className="text-sm text-lum-muted line-through">{format(product.single_price * product.pack_count)}</span>
              <span className="text-xs uppercase tracking-[0.22em] text-lum-sale">Save {format(savings)}</span>
            </>
          )}
        </div>

        <p className="mt-6 text-lum-text2 leading-relaxed">{product.short_description}</p>

        {/* Pack selector */}
        <div className="mt-8">
          <p className="overline mb-3">Pack size</p>
          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="pack-size-single"
              onClick={() => setPackSize('single')}
              className={`p-4 text-left border ${packSize === 'single' ? 'border-lum-text bg-lum-bg2' : 'border-lum-border'}`}
            >
              <p className="text-sm">Single sheet</p>
              <p className="text-xs text-lum-muted mt-1">{format(product.single_price)} each</p>
            </button>
            <button
              data-testid="pack-size-pack"
              onClick={() => setPackSize('pack')}
              className={`p-4 text-left border relative ${packSize === 'pack' ? 'border-lum-text bg-lum-bg2' : 'border-lum-border'}`}
            >
              <span className="absolute top-2 right-2 text-[10px] uppercase tracking-[0.18em] bg-lum-sale text-lum-bg px-2 py-0.5">Best</span>
              <p className="text-sm">Pack of {product.pack_count}</p>
              <p className="text-xs text-lum-muted mt-1">{format(product.pack_price)} · save {format(savings)}</p>
            </button>
          </div>
        </div>

        {/* Qty */}
        <div className="mt-8 flex items-center gap-4">
          <div className="flex items-center border border-lum-border">
            <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-3"><Minus size={14} /></button>
            <span className="px-5 text-sm">{qty}</span>
            <button data-testid="qty-plus" onClick={() => setQty(qty + 1)} className="px-3 py-3"><Plus size={14} /></button>
          </div>
          <button data-testid="add-to-cart-button" onClick={handleAdd} className="btn-primary flex-1">
            Add to Bag · {format(unitPrice * qty)}
          </button>
        </div>

        <div className="mt-6 space-y-2 text-sm text-lum-text2">
          {product.benefits.map((b) => (
            <p key={b} className="flex items-center gap-2"><Check size={14} className="text-lum-sale" /> {b}</p>
          ))}
        </div>

        <div className="mt-10">
          <Accordion type="single" collapsible defaultValue="ingredients">
            <AccordionItem value="ingredients" className="border-lum-border">
              <AccordionTrigger className="text-xs uppercase tracking-[0.22em]">Key ingredients</AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-4 mt-2">
                  {product.ingredients.map((ing) => (
                    <li key={ing.name}>
                      <p className="font-serif-display text-xl">{ing.name}</p>
                      <p className="text-sm text-lum-text2 mt-1">{ing.benefit}</p>
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="how" className="border-lum-border">
              <AccordionTrigger className="text-xs uppercase tracking-[0.22em]">How to use</AccordionTrigger>
              <AccordionContent>
                <p className="text-sm text-lum-text2">Cleanse skin. Apply mask, smooth into place. Relax 15-20 minutes. Remove and pat residual essence into skin. Use 2-3x weekly.</p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="full" className="border-lum-border">
              <AccordionTrigger className="text-xs uppercase tracking-[0.22em]">Full description</AccordionTrigger>
              <AccordionContent>
                <p className="text-sm text-lum-text2 leading-relaxed">{product.long_description}</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
}
